import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useStore } from '../stores/useStore';

const STEP_ORDER = ['welcome', 'clan', 'bot_setup', 'completed'];
const MAIN_ACCESS_CODE = 'M-4829';

const DEFAULT_CLAN_LORE = {
  Neutral: 'Supervivientes independientes. Comercian, exploran y mantienen equilibrio entre facciones.',
  Renegados: 'Nacidos del colapso de las megaciudades. Atacan primero y negocian despues.',
  Oscuros: 'Especialistas en guerra de informacion, sabotaje y control silencioso del territorio.',
  Diablos: 'Choque frontal, doctrina de asalto y supremacia por aniquilacion total.',
  Elegidos: 'Guardianes tecnocraticos. Defienden enclaves clave y lineas de suministro.'
};

const ARMOR_HOTSPOTS = [
  { key: 'p_head', label: 'Cabeza', top: '18%', left: '58%' },
  { key: 'p_torso', label: 'Torso', top: '40%', left: '58%' },
  { key: 'p_shoulders', label: 'Hombros', top: '31%', left: '9%' },
  { key: 'p_arms', label: 'Brazos', top: '50%', left: '9%' },
  { key: 'p_legs', label: 'Piernas', top: '77%', left: '15%' }
];

const STRIKE_POINTS = [
  { id: 'na', x: 18, y: 28, label: 'NORAD' },
  { id: 'eu', x: 48, y: 24, label: 'EURO GRID' },
  { id: 'ru', x: 66, y: 18, label: 'SIBERIA NODE' },
  { id: 'af', x: 50, y: 46, label: 'AFRICA RELAY' },
  { id: 'as', x: 78, y: 34, label: 'PACIFIC' },
  { id: 'sa', x: 27, y: 58, label: 'SOUTH NET' }
];

const BOOT_SEQUENCE = [
  { delay: 500, type: 'system', text: 'WOPR LINK EMULATION V2.4' },
  { delay: 1600, type: 'system', text: 'MAIN CENTRAL IA ONLINE' },
  { delay: 2800, type: 'system', text: 'MARS COMMAND GRID: ACTIVE' },
  { delay: 4300, type: 'warning', text: 'UNAUTHORIZED ACCESS DETECTED' },
  { delay: 6000, type: 'system', text: 'RECOVERED ACCESS FRAGMENT: M-4829' },
  { delay: 7600, type: 'warning', text: 'CONNECT TO MAIN TO CONTINUE' },
  { delay: 9300, type: 'system', text: 'MAIN ACCESS PORT READY' },
  { delay: 10600, type: 'system', text: 'ENTER OPERATOR ALIAS AND ACCESS CODE' }
];

function nextStep(step) {
  const idx = STEP_ORDER.indexOf(step);
  if (idx < 0) return 'welcome';
  return STEP_ORDER[Math.min(idx + 1, STEP_ORDER.length - 1)];
}

function normalizeCommand(command) {
  return String(command || '').trim().replace(/\s+/g, ' ');
}

function getFreeBots(operatorName) {
  return [
    { name: 'NX-12', status: 'FREE' },
    { name: 'KDK-ALPHA', status: 'FREE' },
    { name: operatorName, status: 'UNASSIGNED' }
  ];
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useStore();
  const terminalBottomRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('welcome');
  const [stateData, setStateData] = useState(null);
  const [clans, setClans] = useState([]);
  const [selectedClanId, setSelectedClanId] = useState(null);
  const [error, setError] = useState('');

  const [introStage, setIntroStage] = useState('boot');
  const [mainAlias, setMainAlias] = useState('');
  const [mainCode, setMainCode] = useState('');
  const [terminalInput, setTerminalInput] = useState('');
  const [terminalLogs, setTerminalLogs] = useState([]);
  const [batteryEnabled, setBatteryEnabled] = useState(false);
  const [backpackEnabled, setBackpackEnabled] = useState(false);
  const [claimUnlocked, setClaimUnlocked] = useState(false);

  const [botName, setBotName] = useState('');
  const [armor, setArmor] = useState({
    p_head: 1,
    p_torso: 2,
    p_shoulders: 1,
    p_arms: 1,
    p_legs: 1
  });

  const operatorName = useMemo(() => user?.username || 'OPERADOR', [user?.username]);

  const pointsSpent = useMemo(
    () => Object.values(armor).reduce((acc, n) => acc + Number(n || 0), 0),
    [armor]
  );
  const maxPoints = 300;
  const pointsRemaining = maxPoints - pointsSpent;
  const selectedClanName = useMemo(() => {
    const fromState = stateData?.selectedClan?.name;
    if (fromState) return fromState;
    const found = clans.find((c) => Number(c.id) === Number(selectedClanId));
    return found?.name || 'Neutral';
  }, [stateData?.selectedClan?.name, clans, selectedClanId]);

  function pushTerminalLog(type, text) {
    setTerminalLogs((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        type,
        text
      }
    ]);
  }

  function resetTerminalLogs() {
    setTerminalLogs([]);
  }

  function resetIntroGame() {
    setBatteryEnabled(false);
    setBackpackEnabled(false);
    setClaimUnlocked(false);
    setMainCode('');
    setTerminalInput('');
    resetTerminalLogs();
  }

  async function loadState() {
    setLoading(true);
    setError('');
    try {
      const [stateRes, clansRes] = await Promise.all([
        api.get('/onboarding/state'),
        api.get('/onboarding/clans')
      ]);
      setStateData(stateRes.data);
      setClans(clansRes.data.clans || []);
      setSelectedClanId(stateRes.data?.onboarding?.selectedClanId || null);
      setStep(stateRes.data?.onboarding?.step || 'welcome');

      if (stateRes.data?.onboarding?.completed && stateRes.data?.activeBot) {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar onboarding');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.username) return;
    setBotName((current) => current || user.username);
    setMainAlias((current) => current || user.username);
  }, [user?.username]);

  useEffect(() => {
    if (step !== 'welcome') return;
    if (introStage === 'boot') resetIntroGame();
  }, [step, introStage]);

  useEffect(() => {
    if (step !== 'welcome' || introStage !== 'boot') return undefined;

    const timers = BOOT_SEQUENCE.map((entry) => (
      setTimeout(() => {
        pushTerminalLog(entry.type, entry.text);
      }, entry.delay)
    ));
    const loginTimer = setTimeout(() => {
      setIntroStage('main-login');
    }, 11200);

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      clearTimeout(loginTimer);
    };
  }, [step, introStage]);

  useEffect(() => {
    terminalBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [terminalLogs, introStage]);

  async function handleIntroSeen() {
    setError('');
    try {
      await api.post('/onboarding/intro-seen');
      setStep(nextStep(step));
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo continuar');
    }
  }

  async function handleSelectClan() {
    setError('');
    if (!selectedClanId) {
      setError('Selecciona un clan');
      return;
    }
    try {
      await api.post('/onboarding/select-clan', { clanId: selectedClanId });
      const selectedClan = clans.find((c) => Number(c.id) === Number(selectedClanId)) || null;
      setStateData((prev) => ({
        ...(prev || {}),
        onboarding: {
          ...(prev?.onboarding || {}),
          selectedClanId
        },
        selectedClan
      }));
      setStep('bot_setup');
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo seleccionar clan');
    }
  }

  async function handleCreateBot() {
    setError('');
    const finalBotName = botName.trim() || operatorName;
    if (!finalBotName) {
      setError('Nombre del bot requerido');
      return;
    }
    if (pointsSpent > maxPoints) {
      setError(`Excedes el maximo de puntos (${maxPoints})`);
      return;
    }
    if (pointsSpent < maxPoints) {
      setError(`Debes asignar exactamente ${maxPoints} puntos`);
      return;
    }
    try {
      await api.post('/onboarding/create-bot', {
        botName: finalBotName,
        armorPoints: armor
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo crear el bot');
    }
  }

  function adjustArmor(part, delta) {
    setArmor((prev) => {
      const currentTotal = Object.values(prev).reduce((acc, n) => acc + Number(n || 0), 0);
      const current = Number(prev[part] || 1);
      if (delta < 0) {
        if (current <= 1) return prev;
        return { ...prev, [part]: current - 1 };
      }
      if (currentTotal >= maxPoints) return prev;
      return { ...prev, [part]: current + 1 };
    });
  }

  function setArmorPart(part, value) {
    setArmor((prev) => {
      const current = Number(prev[part] || 1);
      const otherTotal = Object.entries(prev)
        .filter(([key]) => key !== part)
        .reduce((acc, [, n]) => acc + Number(n || 0), 0);
      const maxForPart = Math.max(1, maxPoints - otherTotal);
      const parsed = Number(value);
      const nextValue = Number.isInteger(parsed) ? parsed : current;

      return {
        ...prev,
        [part]: Math.min(maxForPart, Math.max(1, nextValue))
      };
    });
  }

  function handleMainLogin(event) {
    event.preventDefault();
    setError('');

    if (!mainCode.trim()) {
      pushTerminalLog('warning', 'ACCESS CODE REQUIRED');
      return;
    }

    if (normalizeCommand(mainCode).toUpperCase() !== MAIN_ACCESS_CODE) {
      pushTerminalLog('error', 'ACCESS DENIED');
      pushTerminalLog('error', 'INVALID MAIN CODE');
      return;
    }

    pushTerminalLog('user', `ALIAS ${mainAlias.trim() || operatorName}`);
    pushTerminalLog('user', `CODE ${normalizeCommand(mainCode).toUpperCase()}`);
    pushTerminalLog('success', `ACCESS GRANTED FOR ${mainAlias.trim() || operatorName}`);
    pushTerminalLog('system', 'MAIN SESSION OPEN');
    pushTerminalLog('system', 'TYPE help TO SEE COMMANDS');
    setIntroStage('terminal');
  }

  function handleTerminalCommand(event) {
    event.preventDefault();
    setError('');

    const command = normalizeCommand(terminalInput);
    if (!command) return;

    pushTerminalLog('user', `> ${command}`);
    const lowered = command.toLowerCase();

    if (lowered === 'help' || lowered === '/help') {
      pushTerminalLog(
        'system',
        [
          'AVAILABLE COMMANDS:',
          'help',
          'status',
          'bots',
          'scan',
          'battery on',
          'backpack on',
          'claim <name>',
          'clear'
        ].join('\n')
      );
      setTerminalInput('');
      return;
    }

    if (lowered === 'clear' || lowered === '/clear') {
      setTerminalLogs([]);
      setTerminalInput('');
      return;
    }

    if (lowered === 'status' || lowered === '/status') {
      pushTerminalLog(
        'system',
        [
          'MAIN LINK: ONLINE',
          `BATTERY: ${batteryEnabled ? 'ON' : 'OFF'}`,
          `BACKPACK: ${backpackEnabled ? 'ON' : 'OFF'}`,
          `CLAIM READY: ${batteryEnabled && backpackEnabled ? 'YES' : 'NO'}`
        ].join('\n')
      );
      setTerminalInput('');
      return;
    }

    if (lowered === 'bots' || lowered === '/bots') {
      const bots = getFreeBots(operatorName);
      pushTerminalLog(
        'system',
        ['FREE BOTS:', ...bots.map((bot) => `- ${bot.name} [${bot.status}]`)].join('\n')
      );
      setTerminalInput('');
      return;
    }

    if (lowered === 'scan' || lowered === '/scan') {
      pushTerminalLog(
        'system',
        [
          `TARGET UNIT: ${operatorName}`,
          `ACCESS STATE: ${claimUnlocked ? 'CONTROLLED' : 'LOCKED'}`,
          `BATTERY MODULE: ${batteryEnabled ? 'READY' : 'OFFLINE'}`,
          `BACKPACK MODULE: ${backpackEnabled ? 'READY' : 'OFFLINE'}`
        ].join('\n')
      );
      setTerminalInput('');
      return;
    }

    if (lowered === 'battery on' || lowered === '/battery on') {
      setBatteryEnabled(true);
      pushTerminalLog('success', 'BATTERY ENABLED');
      setTerminalInput('');
      return;
    }

    if (lowered === 'backpack on' || lowered === '/backpack on') {
      setBackpackEnabled(true);
      pushTerminalLog('success', 'BACKPACK ENABLED');
      setTerminalInput('');
      return;
    }

    if (lowered.startsWith('claim') || lowered.startsWith('/claim')) {
      const target = lowered.startsWith('/claim') ? command.slice(6).trim() : command.slice(5).trim();
      if (!target) {
        pushTerminalLog('error', 'ACCESS DENIED');
        pushTerminalLog('error', 'MISSING TARGET NAME');
        setTerminalInput('');
        return;
      }

      if (normalizeCommand(target).toLowerCase() !== operatorName.toLowerCase()) {
        pushTerminalLog('error', 'ACCESS DENIED');
        pushTerminalLog('error', `BOT ${target} NOT FOUND`);
        setTerminalInput('');
        return;
      }

      if (!batteryEnabled || !backpackEnabled) {
        pushTerminalLog('error', 'ACCESS DENIED');
        pushTerminalLog('error', 'UNIT INCOMPLETE');
        pushTerminalLog('error', `BATTERY: ${batteryEnabled ? 'ON' : 'OFF'}`);
        pushTerminalLog('error', `BACKPACK: ${backpackEnabled ? 'ON' : 'OFF'}`);
        pushTerminalLog('warning', 'HINT: ACTIVATE BATTERY AND MOCHILA BEFORE CLAIM');
        setTerminalInput('');
        return;
      }

      setClaimUnlocked(true);
      pushTerminalLog('success', 'VERIFYING UNIT...');
      pushTerminalLog('success', `BOT ${operatorName} FOUND`);
      pushTerminalLog('success', 'CONTROL TRANSFERRED');
      pushTerminalLog('success', 'ACCESS GRANTED');
      setIntroStage('complete');
      setTerminalInput('');
      return;
    }

    pushTerminalLog('error', 'COMMAND NOT RECOGNIZED');
    pushTerminalLog('warning', 'TYPE help');
    setTerminalInput('');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05070f] text-cyan-200 flex items-center justify-center">
        Inicializando protocolo de piloto...
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen text-slate-100 relative ${step === 'welcome' ? 'bg-black' : 'bg-cover bg-center bg-fixed'}`}
      style={step === 'welcome' ? undefined : { backgroundImage: "url('/assets/onboarding/fondo.jpg')" }}
    >
      <div className={`absolute inset-0 ${step === 'welcome' ? 'bg-black' : 'bg-[#02050d]/80'}`} />
      <div className={`${step === 'welcome' ? 'min-h-screen relative z-10' : 'max-w-6xl mx-auto px-4 py-8 relative z-10'}`}>
        {step !== 'welcome' && (
          <div className="mb-6 border border-cyan-500/30 bg-[#0a1225] rounded-2xl p-4 shadow-[0_0_30px_rgba(0,255,255,0.12)]">
            <h1 className="text-2xl font-bold text-cyan-300 tracking-wide">PROTOCOLO DE DESPLIEGUE - TERRANWAR</h1>
            <p className="text-sm text-cyan-100/70">
              Completa el onboarding para recibir autorizacion de salida al mapa y asignacion de unidad de combate.
            </p>
            <div className="mt-3 text-xs text-cyan-200/70">
              Paso actual: <span className="font-bold text-cyan-300">{step}</span>
            </div>
          </div>
        )}

        {error && step !== 'welcome' && (
          <div className="mb-4 border border-rose-500/50 bg-rose-500/10 text-rose-200 rounded-xl p-3 text-sm">
            {error}
          </div>
        )}

        {step === 'welcome' && (
          <section className="crt-shell relative min-h-screen overflow-hidden bg-black">
            <div className="crt-frame absolute inset-0 m-2 md:m-4 rounded-[28px] border border-cyan-300/20 bg-[#050908] shadow-[inset_0_0_40px_rgba(130,255,210,0.08),0_0_80px_rgba(90,255,190,0.14)]" />

            <div className="crt-screen relative z-10 min-h-screen p-3 md:p-6">
              <div className="relative h-[calc(100vh-24px)] md:h-[calc(100vh-48px)] overflow-hidden rounded-[22px] border border-cyan-300/18 bg-[#020403]">
                <div className="absolute inset-0 crt-map-glow" />
                <div className="absolute inset-0 crt-grid opacity-25" />
                <div className="absolute inset-0 crt-scanlines" />
                <div className="absolute inset-0 crt-noise opacity-20" />
                <div className="absolute inset-0 crt-vignette" />
                <div className="absolute inset-0 crt-reflection" />

                <div className="absolute inset-[8%_6%_18%_6%] pointer-events-none opacity-55">
                  <svg viewBox="0 0 100 70" className="h-full w-full">
                    <path d="M4 22 L15 16 L29 15 L34 19 L39 18 L47 13 L56 15 L64 12 L71 14 L82 18 L95 17" fill="none" stroke="rgba(110,255,210,0.28)" strokeWidth="0.35" />
                    <path d="M9 30 L14 26 L19 27 L20 33 L17 37 L20 43 L26 49 L28 56" fill="none" stroke="rgba(110,255,210,0.22)" strokeWidth="0.35" />
                    <path d="M36 22 L40 29 L47 31 L49 38 L45 45 L46 53 L53 56 L58 52 L61 44 L68 41 L77 42 L84 35 L91 34" fill="none" stroke="rgba(110,255,210,0.24)" strokeWidth="0.35" />
                    <path d="M58 21 L62 20 L69 24 L75 23 L84 22 L90 25 L95 31" fill="none" stroke="rgba(110,255,210,0.22)" strokeWidth="0.35" />
                    <path d="M42 57 L47 60 L50 64 L55 63 L60 60 L63 56" fill="none" stroke="rgba(110,255,210,0.18)" strokeWidth="0.35" />
                    {STRIKE_POINTS.map((point) => (
                      <g key={point.id}>
                        <circle cx={point.x} cy={point.y} r="1.35" fill="rgba(160,255,210,0.85)" />
                        <circle cx={point.x} cy={point.y} r="2.9" fill="none" stroke="rgba(120,255,210,0.45)" strokeWidth="0.22" />
                        <text
                          x={point.x + 1.8}
                          y={point.y - 1.8}
                          fill="rgba(170,255,220,0.68)"
                          fontSize="2.2"
                          letterSpacing="0.6"
                          fontFamily="'Courier New', monospace"
                        >
                          {point.label}
                        </text>
                      </g>
                    ))}
                    <path d="M18 28 Q28 20 48 24 T78 34" fill="none" stroke="rgba(255,196,80,0.5)" strokeWidth="0.3" strokeDasharray="1.4 1.4" />
                    <path d="M66 18 Q56 30 27 58" fill="none" stroke="rgba(110,255,210,0.42)" strokeWidth="0.25" strokeDasharray="1.1 1.1" />
                  </svg>
                </div>

                <div className="relative z-10 border-b border-cyan-300/12 px-4 py-3 flex flex-wrap items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-[0.42em] text-cyan-200/75">
                  <span className="crt-phosphor">WOPR MAIN STRATEGIC RESPONSE</span>
                  <span className="crt-phosphor">OPERATOR {operatorName}</span>
                  <span className="crt-phosphor">{claimUnlocked ? 'CONTROL LINKED' : introStage === 'terminal' ? 'SESSION OPEN' : 'AUTH REQUIRED'}</span>
                </div>

                <div className="relative z-10 border-b border-cyan-300/10 px-4 py-3 flex items-center justify-between font-mono text-[12px] uppercase tracking-[0.28em] text-cyan-200/65">
                  <span className="crt-phosphor">MAIN://CENTRAL/ACCESS</span>
                  <span className="crt-phosphor">{claimUnlocked ? 'GLOBAL CONTROL READY' : 'HANDSHAKE PENDING'}</span>
                </div>

                <div className="relative z-10 flex-1 overflow-y-auto p-4 md:p-6 font-mono text-[15px] md:text-[16px] leading-7 text-lime-100/90">
                    {terminalLogs.map((entry) => {
                      const palette = {
                        system: 'text-lime-100/90 crt-phosphor',
                        user: 'text-cyan-200 crt-cyan-glow',
                        success: 'text-lime-300 crt-phosphor',
                        warning: 'text-amber-300 crt-amber-glow',
                        error: 'text-rose-300'
                      };

                      return (
                        <div
                          key={entry.id}
                          className={`whitespace-pre-line ${palette[entry.type] || palette.system}`}
                        >
                          {entry.text}
                        </div>
                      );
                    })}

                    {introStage === 'main-login' && (
                      <div className="mt-8 max-w-3xl">
                        <div className="crt-phosphor text-lime-300 uppercase tracking-[0.35em] text-sm">ENTER MAIN CREDENTIALS</div>
                        <form onSubmit={handleMainLogin} className="mt-4 space-y-3">
                          <label className="block">
                            <span className="block text-xs uppercase tracking-[0.35em] text-cyan-200/60 mb-2">Operator Alias</span>
                            <input
                              value={mainAlias}
                              onChange={(e) => setMainAlias(e.target.value)}
                              className="crt-input w-full bg-transparent border-b border-cyan-300/18 px-0 py-2 text-lime-100 focus:outline-none focus:border-cyan-200"
                              placeholder={operatorName}
                            />
                          </label>
                          <label className="block">
                            <span className="block text-xs uppercase tracking-[0.35em] text-cyan-200/60 mb-2">Access Code</span>
                            <input
                              value={mainCode}
                              onChange={(e) => setMainCode(e.target.value)}
                              className="crt-input w-full bg-transparent border-b border-cyan-300/18 px-0 py-2 text-lime-100 focus:outline-none focus:border-cyan-200 tracking-[0.35em]"
                              placeholder="M-4829"
                            />
                          </label>
                          <button
                            type="submit"
                            className="crt-button mt-2 px-5 py-2 border border-cyan-300/24 text-lime-100 bg-cyan-300/6 hover:bg-cyan-300/12"
                          >
                            CONNECT TO MAIN
                          </button>
                        </form>
                      </div>
                    )}

                    {introStage === 'terminal' && (
                      <form onSubmit={handleTerminalCommand} className="mt-8 flex items-center gap-3">
                        <span className="crt-phosphor text-lime-300 shrink-0">{'MAIN>'}</span>
                        <input
                          value={terminalInput}
                          onChange={(e) => setTerminalInput(e.target.value)}
                          className="crt-input flex-1 bg-transparent border-b border-cyan-300/18 px-0 py-2 text-lime-100 focus:outline-none focus:border-cyan-200"
                          placeholder="help"
                          autoComplete="off"
                          spellCheck="false"
                        />
                        <button
                          type="submit"
                          className="crt-button px-4 py-2 border border-cyan-300/24 text-lime-100 bg-cyan-300/6 hover:bg-cyan-300/12"
                        >
                          EXEC
                        </button>
                      </form>
                    )}

                    {introStage === 'complete' && (
                      <div className="mt-6 max-w-2xl">
                        <div className="crt-phosphor text-cyan-200 uppercase tracking-[0.35em] text-xs">Main Confirmation</div>
                        <div className="mt-3 text-2xl text-lime-200 crt-phosphor">BOT CONTROL TRANSFERRED</div>
                        <div className="mt-2 text-lime-100/80 crt-phosphor">
                          MAIN ha vinculado la unidad <span className="text-lime-300">{operatorName}</span> a tu sesion.
                          El siguiente paso es configurar el bot.
                        </div>
                        <button
                          onClick={handleIntroSeen}
                          className="crt-button mt-5 px-5 py-2 border border-cyan-300/24 text-lime-100 bg-cyan-300/6 hover:bg-cyan-300/12"
                        >
                          CONTINUE TO BOT CONFIG
                        </button>
                      </div>
                    )}

                    <div ref={terminalBottomRef} />
                </div>
              </div>
            </div>
          </section>
        )}

        {step === 'clan' && (
          <section>
            <h2 className="text-xl font-bold text-cyan-300 mb-4">Seleccion de Faccion</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {clans.map((clan) => (
                <button
                  key={clan.id}
                  onClick={() => setSelectedClanId(clan.id)}
                  className={`text-left rounded-xl border p-4 transition-all ${
                    Number(selectedClanId) === Number(clan.id)
                      ? 'border-cyan-300 bg-cyan-500/10'
                      : 'border-slate-700 bg-[#0b1020] hover:border-cyan-500/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: clan.color || '#22d3ee' }} />
                    <h3 className="font-bold text-cyan-200">{clan.name}</h3>
                  </div>
                  <p className="text-sm text-slate-300/90">
                    {clan.lore_short || DEFAULT_CLAN_LORE[clan.name] || clan.description || 'Sin historia cargada.'}
                  </p>
                  {clan.motto && <p className="text-xs text-cyan-300/80 mt-2">"{clan.motto}"</p>}
                </button>
              ))}
            </div>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setStep('welcome')}
                className="px-5 py-2 rounded-lg border border-slate-500 bg-slate-500/10 hover:bg-slate-500/20 text-slate-200"
              >
                Volver
              </button>
              <button
                onClick={handleSelectClan}
                className="px-5 py-2 rounded-lg border border-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200"
              >
                Confirmar faccion
              </button>
            </div>
          </section>
        )}

        {step === 'bot_setup' && (
          <section className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
            <div className="border border-cyan-500/30 bg-[#0a1122] rounded-2xl p-5">
              <h2 className="text-xl font-bold text-cyan-300 mb-4">Configuracion de BOT</h2>
              <label className="text-sm text-cyan-100/80 block mb-2">Nombre de unidad</label>
              <input
                value={botName || operatorName}
                readOnly
                className="w-full bg-[#050a14] border border-slate-700 rounded-lg px-3 py-2 text-cyan-100 focus:outline-none focus:border-cyan-400"
              />
              <p className="text-xs text-cyan-100/60 mt-2">
                El bot usa tu nombre de usuario para mantener la identidad de la unidad.
              </p>

              <div className="mt-5">
                <h3 className="font-bold text-cyan-200 mb-2">Distribucion de armadura (Panel interactivo)</h3>
                <div className="relative w-full max-w-[800px] mx-auto border border-cyan-500/40 rounded-xl overflow-hidden">
                  <img
                    src="/assets/onboarding/puntos.jpg"
                    alt="Panel de distribucion de puntos del BOT"
                    className="w-full h-auto block"
                  />
                  {ARMOR_HOTSPOTS.map((part) => (
                    <div
                      key={part.key}
                      className="absolute -translate-y-1/2"
                      style={{ top: part.top, left: part.left }}
                    >
                      <div className="mb-1 text-[11px] font-semibold text-cyan-100 drop-shadow">
                        {part.label}: {armor[part.key]}
                      </div>
                      <div className="flex items-center rounded bg-white/90 text-black overflow-hidden border border-slate-400">
                        <button
                          type="button"
                          onClick={() => adjustArmor(part.key, 1)}
                          disabled={pointsRemaining <= 0}
                          className="px-2 py-0.5 font-bold border-r border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() => adjustArmor(part.key, -1)}
                          disabled={armor[part.key] <= 1}
                          className="px-2 py-0.5 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          -
                        </button>
                      </div>
                      <input
                        type="number"
                        min="1"
                        max={armor[part.key] + pointsRemaining}
                        value={armor[part.key]}
                        onChange={(e) => setArmorPart(part.key, e.target.value)}
                        className="mt-1 w-16 rounded border border-cyan-300/60 bg-[#03111d]/90 px-2 py-1 text-center text-xs font-bold text-cyan-100 shadow-[0_0_12px_rgba(34,211,238,0.35)] focus:outline-none focus:border-cyan-100"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setStep('clan')}
                  className="px-5 py-2 rounded-lg border border-slate-500 bg-slate-500/10 hover:bg-slate-500/20 text-slate-200"
                >
                  Volver
                </button>
              </div>
            </div>

            <div className="border border-cyan-500/30 bg-[#0a1122] rounded-2xl p-5">
              <h3 className="font-bold text-cyan-300 mb-3">Diagnostico</h3>
              <div className="text-sm space-y-2 text-slate-200">
                <div>Puntos usados: <span className="text-cyan-300 font-bold">{pointsSpent}</span> / {maxPoints}</div>
                <div>Puntos restantes: <span className="text-cyan-300 font-bold">{pointsRemaining}</span></div>
                <div>Clan: <span className="text-cyan-300 font-bold">{selectedClanName}</span></div>
                <div>
                  Estado:{' '}
                  <span className="text-cyan-300 font-bold">
                    {pointsSpent === maxPoints ? 'Listo para despliegue' : `Pendiente: faltan ${maxPoints - pointsSpent} puntos`}
                  </span>
                </div>
              </div>
              <button
                onClick={handleCreateBot}
                disabled={pointsSpent !== maxPoints || !operatorName.trim()}
                className="mt-5 w-full px-4 py-2 rounded-lg border border-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Crear unidad y desplegar
              </button>
              <p className="text-xs text-slate-400 mt-3">
                El despliegue inicial sera automatico en la capital de tu faccion o hub neutral.
              </p>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
